import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { completeOAuthRedirect } from "@/lib/auth-callback";
import { takeIntendedPath } from "@/lib/auth-redirect";

export const Route = createFileRoute("/auth/callback")({
  ssr: false,
  staticData: { sitemap: false },
  head: () => ({ meta: [
    { title: "Aanmelding afronden — Gopro Projects" },
    { name: "description", content: "Bezig met het afronden van uw aanmelding." },
    { name: "robots", content: "noindex, nofollow" },
    { property: "og:title", content: "Aanmelding afronden — Gopro Projects" },
    { property: "og:description", content: "Bezig met het afronden van uw aanmelding." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const result = await completeOAuthRedirect();
      if (cancelled) return;

      if (result.status === "error") {
        setError(result.message);
        return;
      }

      if (result.status === "none") {
        // Geen aanmeldbewijs in het adres: enkel doorgaan als er al een sessie is.
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (!data.session) {
          setError("We hebben geen aanmeldgegevens ontvangen. Probeer opnieuw aan te melden.");
          return;
        }
      }

      navigate({ to: takeIntendedPath(), replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold tracking-tight">
          {error ? "Aanmelden is niet gelukt" : "Aanmelding afronden…"}
        </h1>
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {error ?? "Een ogenblik geduld, we ronden uw aanmelding af."}
        </p>
        {error ? (
          <Button className="w-full" onClick={() => navigate({ to: "/auth", replace: true })}>
            Terug naar aanmelden
          </Button>
        ) : null}
      </div>
    </div>
  );
}
